import { useEffect, useState, useCallback, useRef, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { MonitorPlay, WifiOff } from 'lucide-react'
import { screensAPI } from '@/lib/api'
import { useScreenChannel, useScreenRotation } from '@/lib/screensSocket'
import { schermKleur } from '@/lib/screenColors'
import usePageTitle from '@/lib/usePageTitle'

/* De ontvanger blijft ZELF altijd op /scherm/<slug>. De toegewezen inhoud
   komt in een fullscreen iframe. Zo blijft één ding altijd waar:
     — de socket-listener leeft op deze pagina
     — refresh reset alleen de kadertjes, niet de scherm-identiteit
     — de regie kan altijd een nieuwe route naar dit scherm sturen

   Alternatief was `navigate()` doen zoals eerder, maar dan unmount de
   ontvanger en verdwijnt de socket-verbinding — dan werkt de eerste
   opdracht wel en geen enkele daarna. */

const HEARTBEAT_MS = 20_000

export default function ScreenReceiverPage() {
  const { slug } = useParams()
  const [screen, setScreen]   = useState(null)
  const [error, setError]     = useState(null)
  const [route, setRoute]     = useState(null)
  const [clock, setClock]     = useState(() => new Date())

  // Rotatie-instellingen — het scherm loopt zelf door zijn lijstje.
  const [rotatie, setRotatie] = useState({ routes: [], seconds: 30, active: false })

  usePageTitle(screen?.name ? `Scherm · ${screen.name}` : `Scherm · ${slug}`)

  const beat = useCallback(async () => {
    try {
      const { data } = await screensAPI.heartbeat(slug)
      const sc = data?.screen ?? null
      setScreen(sc)
      if (sc) {
        // Alleen updaten als de regie iets anders wil dan we nu tonen;
        // zo geen onnodige iframe-reloads.
        setRoute(prev => prev !== (sc.current_route ?? null) ? (sc.current_route ?? null) : prev)
        setRotatie({
          routes:  Array.isArray(sc.rotation_routes) ? sc.rotation_routes : [],
          seconds: sc.rotation_seconds ?? 30,
          active:  !!sc.rotation_active,
        })
      }
      setError(null)
      return sc
    } catch (e) {
      setError(e.response?.data?.error ?? e.message)
      return null
    }
  }, [slug])

  useEffect(() => { beat() }, [beat])
  useEffect(() => {
    const t = setInterval(beat, HEARTBEAT_MS)
    return () => clearInterval(t)
  }, [beat])

  // Live commando's vanuit de regie — dit blijft nu WERKEN omdat de
  // ontvanger nooit unmount, ook al staat er inhoud in de iframe.
  useScreenChannel(slug, ({ route: nieuw }) => {
    setRoute(nieuw ?? null)
  })

  useScreenRotation(slug, ({ routes, seconds, active }) => {
    setRotatie({ routes: routes ?? [], seconds: seconds ?? 30, active: !!active })
    rotIndex.current = 0
  })

  // Auto-switch draait nu server-side — de backend stuurt gewoon
  // `screen:navigate` events op het interval. Zo weet ook de regie
  // altijd welke route er echt live is. De rotatie-info hier houden
  // we nog wel om de standby-tekst kloppend te krijgen.

  // Klok voor het standby-scherm.
  useEffect(() => {
    const t = setInterval(() => setClock(new Date()), 1000)
    return () => clearInterval(t)
  }, [])

  // Sessie-cache-buster — één keer per mount van deze pagina. Vroeger was dit
  // een hash van de route (dus constant per route). Gevolg: OBS-Chromium cachet
  // de iframe-URL onbeperkt en blijft na een frontend-rebuild oude bundle-hashes
  // laden (404) — schermen worden zwart tot user manueel de source refresht.
  // Met `Date.now()` bij mount is elke OBS-refresh een nieuwe iframe-URL en dus
  // een verse fetch die de nieuwe assets pakt. MOET boven de early-return staan
  // om React's hooks-rules te respecteren (elke render dezelfde volgorde).
  const sessieCb = useMemo(() => `v=${Date.now()}`, [])

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-8">
        <div className="text-center max-w-md">
          <WifiOff className="h-10 w-10 mx-auto text-signal mb-4" />
          <h1 className="text-xl font-bold mb-2">Scherm niet gevonden</h1>
          <p className="text-sm text-muted-foreground">
            Er is geen scherm geregistreerd met slug <code className="font-mono text-acid">{slug}</code>.
            Voeg het toe via Schermregie.
          </p>
        </div>
      </div>
    )
  }

  // Inhoud toegewezen: fullscreen iframe. `key` op de iframe zorgt dat een
  // wissel naar dezelfde route (bv. na een refresh op deze pagina) niet
  // onnodig herladen wordt; alleen een echte route-wissel triggert reload.
  if (route) {
    // Geef het scherm z'n eigen kleur mee als ?accent=, zodat een admin-dashboard
    // dat via dit scherm draait dezelfde accent-kleur kan tonen als in de regie.
    const accent = schermKleur(screen)
    const sep = route.includes('?') ? '&' : '?'
    const cb = sessieCb
    let base = accent ? `${route}${sep}accent=${encodeURIComponent(accent)}` : route
    // Stage-views (Kempenvision / Image Compo / toernooi) tonen normaal een
    // "klik om te starten"-overlay om audio te unlocken. Een scherm-ontvanger
    // heeft geen user-gesture — voeg autoplay=1 toe zodat de overlay
    // overgeslagen wordt. OBS-Chromium mag audio spelen zonder klik.
    if (/\/(kempenvision|imagecompo)\/stage(\/|$|\?)|\/tournaments\/[^/]+\/stage/.test(route)) {
      base = `${base}${base.includes('?') ? '&' : '?'}autoplay=1`
    }
    const src = `${base}${base.includes('?') ? '&' : '?'}${cb}`
    return (
      <iframe
        key={src}
        src={src}
        title={screen?.name ?? slug}
        style={{
          position: 'fixed', inset: 0, width: '100vw', height: '100vh',
          border: 'none', margin: 0, padding: 0, background: '#000',
        }}
        // Sandbox laat scripts en dezelfde origin toe — nodig voor onze
        // eigen pagina's die WebSocket + fetch doen. Formulieren en modals
        // in de child werken normaal.
        sandbox="allow-scripts allow-same-origin allow-forms allow-modals allow-popups allow-downloads allow-pointer-lock"
        allow="autoplay; fullscreen; midi"
      />
    )
  }

  // Standby: grote klok + naam + een subtiele hint of rotatie klaarstaat.
  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center p-8">
      <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-6">
        Standby
      </div>

      <div className="text-[clamp(4rem,18vw,14rem)] font-bold leading-none tabular-nums tracking-tight">
        {clock.toLocaleTimeString('nl-BE', { hour: '2-digit', minute: '2-digit' })}
      </div>

      <div className="text-sm text-muted-foreground mt-4">
        {clock.toLocaleDateString('nl-BE', { weekday: 'long', day: 'numeric', month: 'long' })}
      </div>

      <div className="flex items-center gap-2 mt-10 px-3 py-1.5 rounded-[4px] border border-border">
        <MonitorPlay className="h-4 w-4 text-acid" />
        <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
          {screen?.name ?? slug}
        </span>
        <span className="w-1 h-1 rounded-full bg-live" />
      </div>

      <p className="text-xs text-muted-foreground mt-4">
        {rotatie.active && rotatie.routes.length > 1
          ? `Loopt door ${rotatie.routes.length} weergaven, elke ${rotatie.seconds} seconden.`
          : 'Wacht op de regie. Dit scherm schakelt automatisch om.'}
      </p>
    </div>
  )
}
